import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Bell, AlertTriangle, CheckCheck, Ghost } from "lucide-react";

const POLL_MS = 60_000;

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Bell icon with red unread badge — shown in CoachLayout header.
 * Polls /api/coaches/notifications every 60s, opens a dropdown with the recent
 * CRITICAL events (player name, pain level, time, link to player detail).
 */
export default function NotificationBell() {
  const [data, setData] = useState({ events: [], unseen_count: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/coaches/notifications"); setData(data); }
    catch (err) { console.warn("[notifications] load failed:", err); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    try { await api.post("/coaches/notifications/seen", { all: true }); await load(); }
    catch (err) { console.warn("[notifications] mark-all failed:", err); }
  };

  return (
    <div ref={ref} className="relative" data-testid="notification-bell">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg text-white/80 hover:text-white hover:bg-white/5 transition"
        title="CRITICAL notifications"
        data-testid="notification-bell-btn"
      >
        <Bell className="h-5 w-5" />
        {data.unseen_count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="notification-bell-badge">
            {data.unseen_count > 9 ? "9+" : data.unseen_count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 rounded-xl shadow-2xl ring-1 ring-white/10 z-50 overflow-hidden" style={{ backgroundColor: "var(--ink-2)" }} data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--ink-border)]" style={{ backgroundColor: "var(--ink-1)" }}>
            <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/70 inline-flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" style={{ color: "var(--brand-secondary)" }} />
              Critical events · last 14 days
            </div>
            {data.unseen_count > 0 && (
              <button onClick={markAll} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1" data-testid="notification-mark-all">
                <CheckCheck className="h-3.5 w-3.5" /> Mark all
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.events.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-white/40">No critical events. Roster's clean.</div>
            ) : (
              data.events.map((e) => {
                const ghost = e.event_type === "ghost_pitcher";
                const sev = e.severity || (ghost ? "advisory" : "critical");
                const Icon = ghost ? Ghost : AlertTriangle;
                const accent = ghost
                  ? (sev === "critical" ? "var(--brand-secondary)" : sev === "warning" ? "#F59E0B" : "#22D3EE")
                  : "var(--brand-secondary)";
                return (
                  <Link
                    key={e.event_id}
                    to={`/dashboard/player/${e.player_user_id}`}
                    onClick={() => setOpen(false)}
                    className="block px-4 py-3 border-b border-[var(--ink-border)] hover:bg-white/[0.04] transition"
                    data-testid={`notification-item-${e.event_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: e.seen ? "transparent" : accent, boxShadow: e.seen ? "none" : `0 0 8px ${accent}` }} />
                      <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: accent }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-display font-bold text-white text-sm truncate">
                            {ghost && (
                              <span className="text-[10px] tracking-[0.2em] font-bold uppercase mr-1.5" style={{ color: accent }}>
                                {sev === "critical" ? "🚨 3-Day Alert" : sev === "warning" ? "Ghost · 2d" : "Ghost · 1d"}
                              </span>
                            )}
                            {e.player_name}
                          </div>
                          <div className="text-[10px] text-white/40 shrink-0 font-mono-num">{timeAgo(e.created_at)}</div>
                        </div>
                        {ghost ? (
                          <div className="text-xs text-white/65 mt-0.5">
                            No check-in for <span className="font-mono-num text-white font-bold">{e.days_dark}</span> day{e.days_dark === 1 ? "" : "s"}
                          </div>
                        ) : (
                          <div className="text-xs text-white/65 mt-0.5">
                            Arm health <span className="font-mono-num text-white font-bold">{e.arm_health}/5</span>
                            {e.pain_during && <span className="ml-2 text-red-400 font-bold">· pain-during-throwing</span>}
                          </div>
                        )}
                        {(e.sharp_pain_zones || []).length > 0 && (
                          <div className="text-[11px] mt-1 font-bold" style={{ color: accent }} data-testid={`bell-sharp-zones-${e.event_id}`}>
                            ⚠ Sharp pain · {e.sharp_pain_zones.map((z) => z.label).join(", ")}
                          </div>
                        )}
                        {e.notes && <div className="text-xs text-white/50 mt-1 line-clamp-2 italic">&ldquo;{e.notes}&rdquo;</div>}
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
